import * as PIXI from 'pixi.js';
import { GameSettings, loadSettings, saveSettings } from '../types/SettingsTypes';
import { getAllTranslations, type GameLanguage } from '../utils/Translations';

interface CategoryState {
  expanded: boolean;
  header: PIXI.Graphics;
  headerText: PIXI.Text;
  expandIcon: PIXI.Text;
  content: PIXI.Container;
}

export class SettingsMenu {
  private container: PIXI.Container;
  private settings: GameSettings;
  private onSettingsChangeCallback?: (settings: GameSettings) => void;
  
  // Category states
  private categories: Map<string, CategoryState> = new Map();
  private categoryOrder: string[] = []; // Track order of categories
  private panelY: number = 0;
  private panelHeight: number = 600;
  
  // Controls UI Elements
  private enabledCheckbox!: PIXI.Graphics;
  private speedValueText!: PIXI.Text;
  private speedXValueText!: PIXI.Text;
  private invertedCheckbox!: PIXI.Graphics;
  private invertedXCheckbox!: PIXI.Graphics;
  private minYValueText!: PIXI.Text;
  private maxYValueText!: PIXI.Text;
  private minXValueText!: PIXI.Text;
  private maxXValueText!: PIXI.Text;
  // Audio UI Elements
  private masterVolumeText!: PIXI.Text;
  private musicVolumeText!: PIXI.Text;
  private sfxVolumeText!: PIXI.Text;
  private saveButton!: PIXI.Graphics;
  private closeButton!: PIXI.Graphics;
  private backToMenuButton?: PIXI.Graphics;
  private saveButtonText?: PIXI.Text;
  private closeButtonText?: PIXI.Text;
  private backToMenuText?: PIXI.Text;
  // Gameplay UI Elements
  private gameplayQuickSellCheckbox?: PIXI.Graphics;
  private gameplayQuickBuildCheckbox?: PIXI.Graphics;
  private gameplayInstructionsCheckbox?: PIXI.Graphics;
  
  // Language UI Elements
  private languageButtons: Map<GameLanguage, PIXI.Graphics> = new Map();
  private languageTexts: Map<GameLanguage, PIXI.Text> = new Map();

  private onBackToMenuCallback?: () => void;

  constructor() {
    this.container = new PIXI.Container();
    this.container.visible = false;
    this.settings = loadSettings();
    this.setupMenu();
  }

  private setupMenu(): void {
    // Use 1080p screen dimensions
    const screenWidth = 1920;
    const screenHeight = 1080;
    const panelWidth = 500;
    this.panelHeight = 600;
    
    // Background overlay with pattern
    const overlay = new PIXI.Graphics();
    overlay.beginFill(0x000000, 0.85);
    overlay.drawRect(0, 0, screenWidth, screenHeight);
    overlay.endFill();
    
    // Add decorative pattern (grid-like background)
    overlay.lineStyle(1, 0x1a1a1a, 0.3);
    const gridSize = 40;
    for (let x = 0; x < screenWidth; x += gridSize) {
      overlay.moveTo(x, 0);
      overlay.lineTo(x, screenHeight);
    }
    for (let y = 0; y < screenHeight; y += gridSize) {
      overlay.moveTo(0, y);
      overlay.lineTo(screenWidth, y);
    }
    
    // Add some decorative circles
    overlay.lineStyle(2, 0x2a2a2a, 0.2);
    for (let i = 0; i < 8; i++) {
      const x = (screenWidth / 8) * i + screenWidth / 16;
      const y = screenHeight / 2;
      overlay.drawCircle(x, y, 100 + i * 20);
    }
    
    overlay.eventMode = 'passive'; // Changed to passive to prevent blocking
    overlay.on('pointerdown', (e) => {
      // Only stop propagation if clicking on the overlay itself (not on child elements)
      if (e.target === overlay) {
        e.stopPropagation();
      }
    });
    this.container.addChild(overlay);

    // Panel background - centered on screen
    const panel = new PIXI.Graphics();
    panel.beginFill(0x222222, 0.95);
    panel.drawRect(0, 0, panelWidth, this.panelHeight);
    panel.endFill();
    panel.lineStyle(3, 0x888888);
    panel.drawRect(0, 0, panelWidth, this.panelHeight);
    panel.x = (screenWidth - panelWidth) / 2; // Center horizontally
    this.panelY = (screenHeight - this.panelHeight) / 2; // Center vertically
    panel.y = this.panelY;
    this.container.addChild(panel);

    // Title
    const title = new PIXI.Text('Settings', {
      fontFamily: 'Courier New',
      fontSize: 31,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    title.x = panel.x + 20;
    title.y = this.panelY + 20;
    this.container.addChild(title);

    let yPos = this.panelY + 70;

    // Controls Category
    yPos = this.createCategory('Controls', panel.x, yPos, panelWidth - 40, () => {
      return this.createControlsContent(panel.x + 20, yPos + 40);
    });

    // Graphics Category (Placeholder)
    yPos = this.createCategory('Graphics', panel.x, yPos, panelWidth - 40, () => {
      return this.createPlaceholderContent(panel.x + 20, yPos + 40, 'Graphics settings coming soon...');
    });

    // Audio Category
    yPos = this.createCategory('Audio', panel.x, yPos, panelWidth - 40, () => {
      return this.createAudioContent(panel.x + 20, yPos + 40);
    });

    // Gameplay Category
    yPos = this.createCategory('Gameplay', panel.x, yPos, panelWidth - 40, () => {
      return this.createGameplayContent(panel.x + 20, yPos + 40);
    });

    // Language Category
    yPos = this.createCategory('Language', panel.x, yPos, panelWidth - 40, () => {
      return this.createLanguageContent(panel.x + 20, yPos + 40);
    });

    // Save and Close buttons
    yPos += 20;
    this.createSaveCloseButtons(panel.x, yPos, panelWidth);
  }

  private createCategory(name: string, x: number, y: number, width: number, contentFactory: () => PIXI.Container): number {
    const headerHeight = 35;
    const header = new PIXI.Graphics();
    header.beginFill(0x333333);
    header.drawRect(0, 0, width, headerHeight);
    header.endFill();
    header.lineStyle(2, 0x666666);
    header.drawRect(0, 0, width, headerHeight);
    header.x = x + 20;
    header.y = y;
    header.eventMode = 'static';
    header.cursor = 'pointer';
    this.container.addChild(header);

    const headerText = new PIXI.Text(name, {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    headerText.x = x + 30;
    headerText.y = y + 8;
    this.container.addChild(headerText);

    const expandIcon = new PIXI.Text('▶', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    expandIcon.x = x + width - 30;
    expandIcon.y = y + 10;
    this.container.addChild(expandIcon);

    const content = contentFactory();
    content.visible = false; // Start collapsed
    this.container.addChild(content);

    const categoryState: CategoryState = {
      expanded: false, // Start collapsed
      header,
      headerText,
      expandIcon,
      content,
    };

    header.on('pointerdown', () => {
      categoryState.expanded = !categoryState.expanded;
      content.visible = categoryState.expanded;
      expandIcon.text = categoryState.expanded ? '▼' : '▶';
      // Reposition all categories below this one
      this.repositionCategories();
    });

    this.categories.set(name, categoryState);
    this.categoryOrder.push(name);

    // Return next Y position (header + content height)
    const contentHeight = content.height || 0;
    return y + headerHeight + (categoryState.expanded ? contentHeight : 0) + 10;
  }

  private createControlsContent(x: number, y: number): PIXI.Container {
    const container = new PIXI.Container();
    container.x = x;
    container.y = y;
    let localY = 0;

    // Mouse Wheel Panning Section
    const sectionTitle = new PIXI.Text('Mouse Wheel Panning', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xcccccc,
      fontWeight: 'bold',
    });
    sectionTitle.y = localY;
    container.addChild(sectionTitle);
    localY += 30;

    // Enable/Disable checkbox
    this.enabledCheckbox = new PIXI.Graphics();
    this.enabledCheckbox.beginFill(this.settings.mouseWheelPanEnabled ? 0x00ff00 : 0x666666);
    this.enabledCheckbox.drawRect(0, 0, 20, 20);
    this.enabledCheckbox.endFill();
    this.enabledCheckbox.lineStyle(2, 0xffffff);
    this.enabledCheckbox.drawRect(0, 0, 20, 20);
    this.enabledCheckbox.y = localY;
    this.enabledCheckbox.eventMode = 'static';
    this.enabledCheckbox.cursor = 'pointer';
    this.enabledCheckbox.on('pointerdown', () => {
      this.settings.mouseWheelPanEnabled = !this.settings.mouseWheelPanEnabled;
      this.updateEnabledCheckbox();
    });
    container.addChild(this.enabledCheckbox);

    const enabledText = new PIXI.Text('Enabled', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    enabledText.x = 30;
    enabledText.y = localY + 3;
    container.addChild(enabledText);
    localY += 30;

    // Vertical Pan Speed
    const speedLabel = new PIXI.Text('Vertical Pan Speed:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
    });
    speedLabel.y = localY;
    container.addChild(speedLabel);
    localY += 20;

    const speedControls = this.createNumberControl(0, localY, this.settings.mouseWheelPanSpeed, 10, 200, 10, (value) => {
      this.settings.mouseWheelPanSpeed = value;
      this.updateSpeedText();
    });
    container.addChild(speedControls.container);
    this.speedValueText = speedControls.valueText;
    localY += 35;

    // Horizontal Pan Speed
    const speedXLabel = new PIXI.Text('Horizontal Pan Speed:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
    });
    speedXLabel.y = localY;
    container.addChild(speedXLabel);
    localY += 20;

    const speedXControls = this.createNumberControl(0, localY, this.settings.mouseWheelPanSpeedX, 10, 200, 10, (value) => {
      this.settings.mouseWheelPanSpeedX = value;
      this.updateSpeedXText();
    });
    container.addChild(speedXControls.container);
    this.speedXValueText = speedXControls.valueText;
    localY += 35;

    // Inverted Direction (Vertical)
    this.invertedCheckbox = this.createCheckbox(0, localY, this.settings.mouseWheelPanInverted, () => {
      this.settings.mouseWheelPanInverted = !this.settings.mouseWheelPanInverted;
      this.updateInvertedCheckbox();
    });
    container.addChild(this.invertedCheckbox);

    const invertedText = new PIXI.Text('Invert Vertical Direction', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    invertedText.x = 30;
    invertedText.y = localY + 3;
    container.addChild(invertedText);
    localY += 30;

    // Inverted Direction (Horizontal)
    this.invertedXCheckbox = this.createCheckbox(0, localY, this.settings.mouseWheelPanInvertedX, () => {
      this.settings.mouseWheelPanInvertedX = !this.settings.mouseWheelPanInvertedX;
      this.updateInvertedXCheckbox();
    });
    container.addChild(this.invertedXCheckbox);

    const invertedXText = new PIXI.Text('Invert Horizontal Direction', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    invertedXText.x = 30;
    invertedXText.y = localY + 3;
    container.addChild(invertedXText);
    localY += 35;

    // Vertical Limits
    const limitsLabel = new PIXI.Text('Vertical Limits:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
    });
    limitsLabel.y = localY;
    container.addChild(limitsLabel);
    localY += 20;

    const minYControls = this.createNumberControl(0, localY, this.settings.mouseWheelPanMinY, -2000, 0, 100, (value) => {
      this.settings.mouseWheelPanMinY = value;
      this.updateMinYText();
    });
    container.addChild(minYControls.container);
    this.minYValueText = minYControls.valueText;

    const minYLabel = new PIXI.Text('Min Y (Up):', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xaaaaaa,
    });
    minYLabel.x = 80;
    minYLabel.y = localY + 5;
    container.addChild(minYLabel);
    localY += 30;

    const maxYControls = this.createNumberControl(0, localY, this.settings.mouseWheelPanMaxY, 0, 2000, 100, (value) => {
      this.settings.mouseWheelPanMaxY = value;
      this.updateMaxYText();
    });
    container.addChild(maxYControls.container);
    this.maxYValueText = maxYControls.valueText;

    const maxYLabel = new PIXI.Text('Max Y (Down):', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xaaaaaa,
    });
    maxYLabel.x = 80;
    maxYLabel.y = localY + 5;
    container.addChild(maxYLabel);
    localY += 35;

    // Horizontal Limits
    const limitsXLabel = new PIXI.Text('Horizontal Limits:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
    });
    limitsXLabel.y = localY;
    container.addChild(limitsXLabel);
    localY += 20;

    const minXControls = this.createNumberControl(0, localY, this.settings.mouseWheelPanMinX, -2000, 0, 100, (value) => {
      this.settings.mouseWheelPanMinX = value;
      this.updateMinXText();
    });
    container.addChild(minXControls.container);
    this.minXValueText = minXControls.valueText;

    const minXLabel = new PIXI.Text('Min X (Left):', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xaaaaaa,
    });
    minXLabel.x = 80;
    minXLabel.y = localY + 5;
    container.addChild(minXLabel);
    localY += 30;

    const maxXControls = this.createNumberControl(0, localY, this.settings.mouseWheelPanMaxX, 0, 2000, 100, (value) => {
      this.settings.mouseWheelPanMaxX = value;
      this.updateMaxXText();
    });
    container.addChild(maxXControls.container);
    this.maxXValueText = maxXControls.valueText;

    const maxXLabel = new PIXI.Text('Max X (Right):', {
      fontFamily: 'Courier New',
      fontSize: 14,
      fill: 0xaaaaaa,
    });
    maxXLabel.x = 80;
    maxXLabel.y = localY + 5;
    container.addChild(maxXLabel);

    return container;
  }

  private createGameplayContent(x: number, y: number): PIXI.Container {
    const container = new PIXI.Container();
    container.x = x;
    container.y = y;
    let localY = 0;

    const quickSellTitle = new PIXI.Text('Quick Sell (right click):', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    quickSellTitle.x = 30;
    quickSellTitle.y = localY + 2;
    container.addChild(quickSellTitle);
    
    this.gameplayQuickSellCheckbox = this.createCheckbox(0, localY, this.settings.quickSellEnabled, () => {
      this.settings.quickSellEnabled = !this.settings.quickSellEnabled;
      this.updateGameplayQuickSellCheckbox();
    });
    container.addChild(this.gameplayQuickSellCheckbox);
    localY += 35;

    const quickBuildTitle = new PIXI.Text('Quick Build (over walls):', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    quickBuildTitle.x = 30;
    quickBuildTitle.y = localY + 2;
    container.addChild(quickBuildTitle);
    
    this.gameplayQuickBuildCheckbox = this.createCheckbox(0, localY, this.settings.quickBuildEnabled, () => {
      this.settings.quickBuildEnabled = !this.settings.quickBuildEnabled;
      this.updateGameplayQuickBuildCheckbox();
    });
    container.addChild(this.gameplayQuickBuildCheckbox);
    localY += 35;

    const instructionsTitle = new PIXI.Text('Show instructions on start:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    instructionsTitle.x = 30;
    instructionsTitle.y = localY + 2;
    container.addChild(instructionsTitle);

    this.gameplayInstructionsCheckbox = this.createCheckbox(0, localY, this.settings.showInstructionsOnStart ?? true, () => {
      this.settings.showInstructionsOnStart = !this.settings.showInstructionsOnStart;
      this.updateGameplayInstructionsCheckbox();
    });
    container.addChild(this.gameplayInstructionsCheckbox);
    localY += 35;

    return container;
  }

  private createLanguageContent(x: number, y: number): PIXI.Container {
    const container = new PIXI.Container();
    container.x = x;
    container.y = y;
    let localY = 0;

    const t = getAllTranslations(this.settings.language || 'en');
    
    const languageLabel = new PIXI.Text(t.language, {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    languageLabel.y = localY;
    container.addChild(languageLabel);
    localY += 30;

    const languages: Array<{ code: GameLanguage; name: string }> = [
      { code: 'en', name: t.english },
      { code: 'pt-BR', name: t.portuguese },
      { code: 'fr', name: t.french },
      { code: 'it', name: t.italian },
      { code: 'de', name: t.german },
      { code: 'zh', name: t.chinese },
      { code: 'ru', name: t.russian },
    ];

    const buttonWidth = 200;
    const buttonHeight = 35;
    const buttonSpacing = 8;
    let currentX = 0;
    const maxWidth = 900;
    let rowY = localY;

    languages.forEach((lang, index) => {
      const isSelected = this.settings.language === lang.code;
      
      const button = new PIXI.Graphics();
      button.beginFill(isSelected ? 0x006600 : 0x444444, 0.9);
      button.drawRoundedRect(0, 0, buttonWidth, buttonHeight, 6);
      button.endFill();
      button.lineStyle(2, isSelected ? 0x00aa00 : 0x666666);
      button.drawRoundedRect(0, 0, buttonWidth, buttonHeight, 6);
      button.x = currentX;
      button.y = rowY;
      button.eventMode = 'static';
      button.cursor = 'pointer';
      
      button.on('pointerdown', () => {
        this.settings.language = lang.code;
        this.updateLanguageButtons();
        // Update MainMenu language if it exists
        if ((window as any).mainMenuInstance) {
          (window as any).mainMenuInstance.setLanguage(lang.code);
        }
        // Save settings immediately
        saveSettings(this.settings);
      });
      
      container.addChild(button);
      this.languageButtons.set(lang.code, button);

      const buttonText = new PIXI.Text(lang.name, {
        fontFamily: 'Courier New',
        fontSize: 16,
        fill: 0xffffff,
        align: 'center',
      });
      buttonText.x = currentX + buttonWidth / 2 - buttonText.width / 2;
      buttonText.y = rowY + buttonHeight / 2 - buttonText.height / 2;
      container.addChild(buttonText);
      this.languageTexts.set(lang.code, buttonText);

      currentX += buttonWidth + buttonSpacing;
      if (currentX + buttonWidth > maxWidth) {
        currentX = 0;
        rowY += buttonHeight + buttonSpacing;
      }
    });

    return container;
  }

  private updateLanguageButtons(): void {
    this.languageButtons.forEach((button, code) => {
      const isSelected = this.settings.language === code;
      button.clear();
      button.beginFill(isSelected ? 0x006600 : 0x444444, 0.9);
      button.drawRoundedRect(0, 0, 200, 35, 6);
      button.endFill();
      button.lineStyle(2, isSelected ? 0x00aa00 : 0x666666);
      button.drawRoundedRect(0, 0, 200, 35, 6);
    });
  }

  private createAudioContent(x: number, y: number): PIXI.Container {
    const container = new PIXI.Container();
    container.x = x;
    container.y = y;
    let localY = 0;

    // Master Volume
    const masterLabel = new PIXI.Text('Master Volume:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
    });
    masterLabel.y = localY;
    container.addChild(masterLabel);
    localY += 20;

    const masterControls = this.createVolumeControl(0, localY, Math.round(this.settings.masterVolume * 100), (value) => {
      this.settings.masterVolume = value / 100;
      this.updateMasterVolumeText();
    });
    container.addChild(masterControls.container);
    this.masterVolumeText = masterControls.valueText;
    localY += 35;

    // Music Volume
    const musicLabel = new PIXI.Text('Music Volume:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
    });
    musicLabel.y = localY;
    container.addChild(musicLabel);
    localY += 20;

    const musicControls = this.createVolumeControl(0, localY, Math.round(this.settings.musicVolume * 100), (value) => {
      this.settings.musicVolume = value / 100;
      this.updateMusicVolumeText();
    });
    container.addChild(musicControls.container);
    this.musicVolumeText = musicControls.valueText;
    localY += 35;

    // SFX Volume
    const sfxLabel = new PIXI.Text('SFX Volume:', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xcccccc,
    });
    sfxLabel.y = localY;
    container.addChild(sfxLabel);
    localY += 20;

    const sfxControls = this.createVolumeControl(0, localY, Math.round(this.settings.sfxVolume * 100), (value) => {
      this.settings.sfxVolume = value / 100;
      this.updateSfxVolumeText();
    });
    container.addChild(sfxControls.container);
    this.sfxVolumeText = sfxControls.valueText;

    return container;
  }

  private createVolumeControl(x: number, y: number, initialValue: number, onChange: (value: number) => void): { container: PIXI.Container, valueText: PIXI.Text } {
    const container = new PIXI.Container();
    container.x = x;
    container.y = y;

    // Decrease button
    const downBtn = new PIXI.Graphics();
    downBtn.beginFill(0x444444);
    downBtn.drawRect(0, 0, 25, 20);
    downBtn.endFill();
    downBtn.lineStyle(1, 0x888888);
    downBtn.drawRect(0, 0, 25, 20);
    downBtn.eventMode = 'static';
    downBtn.cursor = 'pointer';
    downBtn.on('pointerdown', () => {
      const newValue = Math.max(0, initialValue - 5);
      onChange(newValue);
      initialValue = newValue;
      valueText.text = `${newValue}%`;
    });
    container.addChild(downBtn);

    const downText = new PIXI.Text('-', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    downText.x = 12.5 - downText.width / 2;
    downText.y = 3;
    container.addChild(downText);

    // Value display
    const valueText = new PIXI.Text(`${initialValue}%`, {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    valueText.x = 35;
    valueText.y = 4;
    container.addChild(valueText);

    // Increase button
    const upBtn = new PIXI.Graphics();
    upBtn.beginFill(0x444444);
    upBtn.drawRect(0, 0, 25, 20);
    upBtn.endFill();
    upBtn.lineStyle(1, 0x888888);
    upBtn.drawRect(0, 0, 25, 20);
    upBtn.x = 80;
    upBtn.eventMode = 'static';
    upBtn.cursor = 'pointer';
    upBtn.on('pointerdown', () => {
      const newValue = Math.min(100, initialValue + 5);
      onChange(newValue);
      initialValue = newValue;
      valueText.text = `${newValue}%`;
    });
    container.addChild(upBtn);

    const upText = new PIXI.Text('+', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    upText.x = 80 + 12.5 - upText.width / 2;
    upText.y = 3;
    container.addChild(upText);

    return { container, valueText };
  }

  private createPlaceholderContent(x: number, y: number, message: string): PIXI.Container {
    const container = new PIXI.Container();
    container.x = x;
    container.y = y;

    const placeholderText = new PIXI.Text(message, {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0x888888,
      fontStyle: 'italic',
    });
    container.addChild(placeholderText);

    return container;
  }

  private createNumberControl(x: number, y: number, initialValue: number, min: number, max: number, step: number, onChange: (value: number) => void): { container: PIXI.Container, valueText: PIXI.Text } {
    const container = new PIXI.Container();
    container.x = x;
    container.y = y;

    // Decrease button
    const downBtn = new PIXI.Graphics();
    downBtn.beginFill(0x444444);
    downBtn.drawRect(0, 0, 25, 20);
    downBtn.endFill();
    downBtn.lineStyle(1, 0x888888);
    downBtn.drawRect(0, 0, 25, 20);
    downBtn.eventMode = 'static';
    downBtn.cursor = 'pointer';
    downBtn.on('pointerdown', () => {
      const newValue = Math.max(min, initialValue - step);
      onChange(newValue);
      initialValue = newValue;
      valueText.text = `${newValue}`;
    });
    container.addChild(downBtn);

    const downText = new PIXI.Text('-', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    downText.x = 12.5 - downText.width / 2;
    downText.y = 3;
    container.addChild(downText);

    // Value display
    const valueText = new PIXI.Text(`${initialValue}`, {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    valueText.x = 35;
    valueText.y = 4;
    container.addChild(valueText);

    // Increase button
    const upBtn = new PIXI.Graphics();
    upBtn.beginFill(0x444444);
    upBtn.drawRect(0, 0, 25, 20);
    upBtn.endFill();
    upBtn.lineStyle(1, 0x888888);
    upBtn.drawRect(0, 0, 25, 20);
    upBtn.x = 80;
    upBtn.eventMode = 'static';
    upBtn.cursor = 'pointer';
    upBtn.on('pointerdown', () => {
      const newValue = Math.min(max, initialValue + step);
      onChange(newValue);
      initialValue = newValue;
      valueText.text = `${newValue}`;
    });
    container.addChild(upBtn);

    const upText = new PIXI.Text('+', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    upText.x = 80 + 12.5 - upText.width / 2;
    upText.y = 3;
    container.addChild(upText);

    return { container, valueText };
  }

  private createCheckbox(x: number, y: number, checked: boolean, onToggle: () => void): PIXI.Graphics {
    const checkbox = new PIXI.Graphics();
    checkbox.beginFill(checked ? 0x00ff00 : 0x666666);
    checkbox.drawRect(0, 0, 20, 20);
    checkbox.endFill();
    checkbox.lineStyle(2, 0xffffff);
    checkbox.drawRect(0, 0, 20, 20);
    checkbox.x = x;
    checkbox.y = y;
    checkbox.eventMode = 'static';
    checkbox.cursor = 'pointer';
    checkbox.on('pointerdown', onToggle);
    return checkbox;
  }

  public onBackToMainMenu(callback: () => void): void {
    this.onBackToMenuCallback = callback;
  }

  private createSaveCloseButtons(x: number, y: number, width: number): void {
    // Save button
    this.saveButton = new PIXI.Graphics();
    this.saveButton.beginFill(0x006600);
    this.saveButton.drawRect(0, 0, 120, 40);
    this.saveButton.endFill();
    this.saveButton.lineStyle(2, 0xffffff);
    this.saveButton.drawRect(0, 0, 120, 40);
    this.saveButton.x = x + (width - 260) / 2;
    this.saveButton.y = y;
    this.saveButton.eventMode = 'static';
    this.saveButton.cursor = 'pointer';
    this.saveButton.on('pointerdown', () => {
      saveSettings(this.settings);
      if (this.onSettingsChangeCallback) {
        this.onSettingsChangeCallback(this.settings);
      }
      this.hide();
    });
    this.container.addChild(this.saveButton);

    this.saveButtonText = new PIXI.Text('Save', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.saveButtonText.x = this.saveButton.x + 60 - this.saveButtonText.width / 2;
    this.saveButtonText.y = this.saveButton.y + 12;
    this.container.addChild(this.saveButtonText);

    // Close button
    this.closeButton = new PIXI.Graphics();
    this.closeButton.beginFill(0x660000);
    this.closeButton.drawRect(0, 0, 120, 40);
    this.closeButton.endFill();
    this.closeButton.lineStyle(2, 0xffffff);
    this.closeButton.drawRect(0, 0, 120, 40);
    this.closeButton.x = x + (width - 260) / 2 + 140;
    this.closeButton.y = y;
    this.closeButton.eventMode = 'static';
    this.closeButton.cursor = 'pointer';
    this.closeButton.on('pointerdown', (e) => {
      e.stopPropagation();
      this.settings = loadSettings();
      this.updateAllControls();
      this.hide();
      // Show main menu after hiding settings
      const mainMenuInstance = (window as any).mainMenuInstance;
      if (mainMenuInstance) {
        mainMenuInstance.show();
      }
    });
    this.container.addChild(this.closeButton);

    this.closeButtonText = new PIXI.Text('Cancel', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.closeButtonText.x = this.closeButton.x + 60 - this.closeButtonText.width / 2;
    this.closeButtonText.y = this.closeButton.y + 12;
    this.container.addChild(this.closeButtonText);

    // Back to Main Menu button
    this.backToMenuButton = new PIXI.Graphics();
    this.backToMenuButton.beginFill(0x333366);
    this.backToMenuButton.drawRect(0, 0, width - 40, 40);
    this.backToMenuButton.endFill();
    this.backToMenuButton.lineStyle(2, 0xffffff);
    this.backToMenuButton.drawRect(0, 0, width - 40, 40);
    this.backToMenuButton.x = x + 20;
    this.backToMenuButton.y = y + 60;
    this.backToMenuButton.eventMode = 'static';
    this.backToMenuButton.cursor = 'pointer';
    this.backToMenuButton.on('pointerdown', () => {
      this.settings = loadSettings();
      this.updateAllControls();
      this.hide();
      this.onBackToMenuCallback?.();
    });
    this.container.addChild(this.backToMenuButton);

    this.backToMenuText = new PIXI.Text('Back to Main Menu', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.backToMenuText.x = this.backToMenuButton.x + (this.backToMenuButton.width / 2) - this.backToMenuText.width / 2;
    this.backToMenuText.y = this.backToMenuButton.y + 12;
    this.container.addChild(this.backToMenuText);
  }

  private updateEnabledCheckbox(): void {
    this.enabledCheckbox.clear();
    this.enabledCheckbox.beginFill(this.settings.mouseWheelPanEnabled ? 0x00ff00 : 0x666666);
    this.enabledCheckbox.drawRect(0, 0, 20, 20);
    this.enabledCheckbox.endFill();
    this.enabledCheckbox.lineStyle(2, 0xffffff);
    this.enabledCheckbox.drawRect(0, 0, 20, 20);
  }

  private updateInvertedCheckbox(): void {
    this.invertedCheckbox.clear();
    this.invertedCheckbox.beginFill(this.settings.mouseWheelPanInverted ? 0x00ff00 : 0x666666);
    this.invertedCheckbox.drawRect(0, 0, 20, 20);
    this.invertedCheckbox.endFill();
    this.invertedCheckbox.lineStyle(2, 0xffffff);
    this.invertedCheckbox.drawRect(0, 0, 20, 20);
  }

  private updateInvertedXCheckbox(): void {
    this.invertedXCheckbox.clear();
    this.invertedXCheckbox.beginFill(this.settings.mouseWheelPanInvertedX ? 0x00ff00 : 0x666666);
    this.invertedXCheckbox.drawRect(0, 0, 20, 20);
    this.invertedXCheckbox.endFill();
    this.invertedXCheckbox.lineStyle(2, 0xffffff);
    this.invertedXCheckbox.drawRect(0, 0, 20, 20);
  }

  private updateSpeedText(): void {
    this.speedValueText.text = `${this.settings.mouseWheelPanSpeed}`;
  }

  private updateSpeedXText(): void {
    this.speedXValueText.text = `${this.settings.mouseWheelPanSpeedX}`;
  }

  private updateMinYText(): void {
    this.minYValueText.text = `${this.settings.mouseWheelPanMinY}`;
  }

  private updateMaxYText(): void {
    this.maxYValueText.text = `${this.settings.mouseWheelPanMaxY}`;
  }

  private updateMinXText(): void {
    this.minXValueText.text = `${this.settings.mouseWheelPanMinX}`;
  }

  private updateMaxXText(): void {
    this.maxXValueText.text = `${this.settings.mouseWheelPanMaxX}`;
  }

  private repositionCategories(): void {
    let currentY = this.panelY + 70;
    const headerHeight = 35;

    for (const categoryName of this.categoryOrder) {
      const category = this.categories.get(categoryName);
      if (!category) continue;

      // Update header position
      category.header.y = currentY;
      category.headerText.y = currentY + 8;
      category.expandIcon.y = currentY + 10;

      // Update content position
      category.content.y = currentY + headerHeight;

      // Move to next position
      const contentHeight = category.content.height || 0;
      currentY += headerHeight + (category.expanded ? contentHeight : 0) + 10;
    }

    // Reposition save/close buttons
    currentY += 20;
    if (this.saveButton && this.closeButton) {
      this.saveButton.y = currentY;
      this.closeButton.y = currentY;
      if (this.saveButtonText) {
        this.saveButtonText.y = currentY + 12;
      }
      if (this.closeButtonText) {
        this.closeButtonText.y = currentY + 12;
      }
    }

    const backButtonSpacing = 60;
    const backButtonY = currentY + backButtonSpacing;
    if (this.backToMenuButton && this.backToMenuText) {
      this.backToMenuButton.y = backButtonY;
      this.backToMenuText.y = backButtonY + 12;
      currentY = backButtonY + this.backToMenuButton.height;
    }
  }

  private updateMasterVolumeText(): void {
    this.masterVolumeText.text = `${Math.round(this.settings.masterVolume * 100)}%`;
  }

  private updateMusicVolumeText(): void {
    this.musicVolumeText.text = `${Math.round(this.settings.musicVolume * 100)}%`;
  }

  private updateSfxVolumeText(): void {
    this.sfxVolumeText.text = `${Math.round(this.settings.sfxVolume * 100)}%`;
  }

  private updateGameplayQuickSellCheckbox(): void {
    if (!this.gameplayQuickSellCheckbox) return;
    this.gameplayQuickSellCheckbox.clear();
    this.gameplayQuickSellCheckbox.beginFill(this.settings.quickSellEnabled ? 0x00ff00 : 0x666666);
    this.gameplayQuickSellCheckbox.drawRect(0, 0, 20, 20);
    this.gameplayQuickSellCheckbox.endFill();
    this.gameplayQuickSellCheckbox.lineStyle(2, 0xffffff);
    this.gameplayQuickSellCheckbox.drawRect(0, 0, 20, 20);
  }

  private updateGameplayQuickBuildCheckbox(): void {
    if (!this.gameplayQuickBuildCheckbox) return;
    this.gameplayQuickBuildCheckbox.clear();
    this.gameplayQuickBuildCheckbox.beginFill(this.settings.quickBuildEnabled ? 0x00ff00 : 0x666666);
    this.gameplayQuickBuildCheckbox.drawRect(0, 0, 20, 20);
    this.gameplayQuickBuildCheckbox.endFill();
    this.gameplayQuickBuildCheckbox.lineStyle(2, 0xffffff);
    this.gameplayQuickBuildCheckbox.drawRect(0, 0, 20, 20);
  }

  private updateGameplayInstructionsCheckbox(): void {
    if (!this.gameplayInstructionsCheckbox) return;
    this.gameplayInstructionsCheckbox.clear();
    const enabled = this.settings.showInstructionsOnStart ?? true;
    this.gameplayInstructionsCheckbox.beginFill(enabled ? 0x00ff00 : 0x666666);
    this.gameplayInstructionsCheckbox.drawRect(0, 0, 20, 20);
    this.gameplayInstructionsCheckbox.endFill();
    this.gameplayInstructionsCheckbox.lineStyle(2, 0xffffff);
    this.gameplayInstructionsCheckbox.drawRect(0, 0, 20, 20);
  }

  private updateAllControls(): void {
    this.updateEnabledCheckbox();
    this.updateInvertedCheckbox();
    this.updateInvertedXCheckbox();
    this.updateSpeedText();
    this.updateSpeedXText();
    this.updateMinYText();
    this.updateMaxYText();
    this.updateMinXText();
    this.updateMaxXText();
    if (this.masterVolumeText) this.updateMasterVolumeText();
    if (this.musicVolumeText) this.updateMusicVolumeText();
    if (this.sfxVolumeText) this.updateSfxVolumeText();
    this.updateGameplayQuickSellCheckbox();
    this.updateGameplayQuickBuildCheckbox();
    this.updateGameplayInstructionsCheckbox();
  }

  show(): void {
    this.container.visible = true;
    this.settings = loadSettings();
    this.updateAllControls();
    // Ensure all categories are properly positioned
    this.repositionCategories();
    // Ensure pointer events are enabled on the canvas
    const infoPagesCanvas = (window as any).infoPagesCanvas;
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'auto';
    }
  }

  hide(): void {
    this.container.visible = false;
    // Disable pointer events when hidden
    const infoPagesCanvas = (window as any).infoPagesCanvas;
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'none';
    }
  }

  onSettingsChange(callback: (settings: GameSettings) => void): void {
    this.onSettingsChangeCallback = callback;
  }

  getSettings(): GameSettings {
    return this.settings;
  }

  getContainer(): PIXI.Container {
    return this.container;
  }
}
